import { currencySymbols } from '../config/currency-symbols';
import { Translations } from '../config/translations';
import { ResourceCache } from '../utils/resource-cache';
import { PaymentType } from '../providers/payment-type';

export class View {

    cacheService = ResourceCache.getInstance();

    getHtmlElementValue(selector: string): string {
        const element = document.querySelector(selector) as HTMLSelectElement;

        return element ? element.value : '';
    }

    getHtmlElement(selector: string): HTMLElement {
        return (document.querySelector(selector) as HTMLElement) || '';
    }

    getInputElement(selector: string): HTMLInputElement {
        return (document.querySelector(selector) as HTMLInputElement) || '';
    }

    getHtmlElements(selector: string): NodeListOf<HTMLInputElement> {
        return (
            (document.querySelectorAll(
                selector
            ) as NodeListOf<HTMLInputElement>) || []
        );
    }

    getHtmlElementBy(baseElement: HTMLElement, selector: string): HTMLElement {
        return (baseElement.querySelector(selector) as HTMLElement) || '';
    }

    getHSFrame(): Document {
        const iframe: HTMLIFrameElement | null = this.getDFContainer().querySelector('[id^="hs-form-iframe"]');

        let innerDoc = document;

        if (iframe) {
            innerDoc =
                iframe.contentDocument ||
                (iframe.contentWindow ? iframe.contentWindow.document : document);
        }

        return innerDoc;
    }

    /**
     * If HubSpot form is rendered as an iframe then search element directly inside an iframe,
     * otherwise search inside "#df-card-container".
     */
    getFromHSFrame(selector: string): HTMLElement {
        const hsFrame = this.getHSFrame();
        if (!this.isHsFormIFrame(hsFrame) && selector.indexOf('#df-card-container') === -1) {
            selector = '#df-card-container ' + selector;
        }
        return <HTMLElement>hsFrame.querySelector(selector);
    }

    isHsFormIFrame(hsFrame: Document) {
        return hsFrame.location !== window.parent.location;
    }

    getDFContainer(): HTMLElement {
        const iframe: HTMLIFrameElement | null = document.querySelector('#df-iframe');
        let innerDoc = document;

        if (iframe) {
            innerDoc =
                iframe.contentDocument ||
                (iframe.contentWindow ? iframe.contentWindow.document : document);
        }

        // @ts-ignore
        return innerDoc.getElementById('df-card-container');
    }

    wrapElementHSStyles(label: string, element: HTMLElement) {
        const wrapped = document.createElement('fieldset');
        wrapped.classList.add('df-fieldset');
        wrapped.innerHTML = `<div class='field hs-form-field df-field'>
                                <label><span class='df-f-label' ${label && 'data-label="pay_with"'
            }>${label}</span></label>
                                <div class='input'>${element.outerHTML}</div>
                            </div>`;

        return wrapped;
    }

    setVisible(isActive: boolean) { }

    show(element: any) {
        element.style.display = 'block';
    }

    hide(element: any) {
        element.style.display = 'none';
    }

    setVal(selector: string, value: string) {
        const element = this.getHtmlElement(selector) as HTMLElement;

        if (element instanceof HTMLInputElement) {
            element.value = value;
            element.dispatchEvent(new Event('input'));
        } else {
            element.innerHTML = value;
        }
    }

    removeElement(id: string) {
        const element = document.getElementById(id);
        if (element) {
            element.outerHTML = '';
        }
    }

    showError(errorMessage: string, selector: string) {
        const element = this.getHtmlElement(selector);
        if (element) {
            element.innerText = errorMessage;
            element.style.display = 'block';
        }
    }

    hideError(selector: string) {
        const element = this.getHtmlElement(selector);
        if (element) {
            element.innerText = '';
            element.style.display = 'none';
        }
    }

    setAmount(amount: string) {
        const totalField = this.getHtmlElement('#df-final-amount');
        totalField.innerText = amount;
    }

    protected formatMoney(amount: string, currency: any): string {
        let finalAmount: any = +amount || 0;
        finalAmount = finalAmount.toFixed(2);
        const minimumFractionDigits = 2;
        finalAmount = parseFloat(finalAmount);
        const symbol: string = currencySymbols[currency];
        return `${symbol}${finalAmount.toLocaleString(undefined, {
            minimumFractionDigits: minimumFractionDigits
        })}`;
    }

    formatNumber(amount: string): string {
        let finalAmount: any = +amount || 0;
        finalAmount = finalAmount.toFixed(2);
        const minimumFractionDigits = 2;
        finalAmount = parseFloat(finalAmount);
        return `${finalAmount.toLocaleString(undefined, {
            minimumFractionDigits: minimumFractionDigits
        })}`;
    }

    loadCss(url: string) {
        if (url && document.head) {
            if (this.cacheService.checkIfCSSLoaded(url)) {
                return;
            }
            this.cacheService.addCSSToCache(url);

            const linkElement = document.createElement('link');
            linkElement.setAttribute('rel', 'stylesheet');
            linkElement.setAttribute('type', 'text/css');
            linkElement.setAttribute('href', url);
            linkElement.setAttribute('data-isdf', 'true');
            document.head.appendChild(linkElement);
        }
    }

    loadThemeCss(url: string) {
        if (url && document.head) {
            if (this.cacheService.checkIfCSSLoaded(url)) {
                return;
            }
            this.cacheService.addCSSToCache(url);

            const linkElement = document.createElement('link');
            linkElement.setAttribute('rel', 'stylesheet');
            linkElement.setAttribute('type', 'text/css');
            linkElement.setAttribute('href', url);
            linkElement.setAttribute('data-isdf', 'true');
            document.head.prepend(linkElement);
        }
    }

    loadCssCode(code: string) {
        if (document.head) {
            const existingDpfCssStyleContainer = document.getElementById('dpf-css-style');
            if (existingDpfCssStyleContainer) {
                document.head.removeChild(existingDpfCssStyleContainer);
            }
            const dpfCssStyleContainer = document.createElement('style');
            dpfCssStyleContainer.setAttribute('id', 'dpf-css-style');
            dpfCssStyleContainer.setAttribute('type', 'text/css');
            dpfCssStyleContainer.textContent = code;
            document.head.appendChild(dpfCssStyleContainer);
        }
    }

    startProgress() {
        const loader = this.getHtmlElement('#dfLoader');
        const button = this.getHtmlElement('#df_buybtn');
        if (loader) {
            loader.classList.add('show');
        }
        if (button) {
            button.setAttribute('value', Translations.PROCESSING);
            button.setAttribute('disabled', 'disable');
        }
    }

    stopProgress(paymentType?: PaymentType) {
        const loader = this.getHtmlElement('#dfLoader');
        const button = this.getHtmlElement('#df_buybtn');
        if (loader) {
            loader.classList.remove('show');
        }
        if (button) {
            button.setAttribute(
                'value',
                this.getPaymentTypeTranslation(paymentType)
            );
            button.removeAttribute('disabled');
        }
    }

    private getPaymentTypeTranslation(paymentType?: PaymentType) {
        switch (paymentType) {
            case PaymentType.OFFLINE:
                return Translations.OFFLINE_PAYMENT;
            default:
                return Translations.PAY_NOW;
        }
    }

    showForm() { }

    hideForm() { }

    renderFormLoader() {
        const loaderHTML = `
                <style>
                    #df-card-container{
                        display: none;
                    }
                    #df-initial-loader-wrapper {
                        max-width: 550px;
                        padding: 4%;
                        max-height: 1000px;
                        background-color: #fff
                    }
                    .df-progress {
                        width: 100%;
                        height: 50px;
                        background: #ededed;
                        border-radius: 10px;
                        overflow: hidden;
                        margin: 10px 0 8px
                    }
                    .df-progress.df-label {
                        width: 30%;
                        height: 17px;
                        border-radius: 3px;
                        margin: 40px 0 10px
                    }
                    .df-progress.df-label.first {
                        margin: 20px 0 10px
                    }
                    .df-progress.df-col-2 {
                        width: 50%;
                        margin-left: 5px;
                        margin-right: 5px
                    }
                    .df-progress.df-button {
                        margin-top: 60px
                    }
                    .df-progress-bar {
                        display: block;
                        height: 100%;
                        background-image: linear-gradient(90deg, #f4f4f4, #f4f4f4 50%, #fff 55%, #fff 56%, #f4f4f4 75%, #f4f4f4);
                        background-size: 400% 100%;
                        animation: progress-animation 2s linear infinite
                    }
                    @keyframes progress-animation {
                        0% {
                            background-position: 100%
                        }
                        100% {
                            background-position: 0
                        }
                    }
                    .df-row {
                        display: flex;
                        margin: 0 -5px
                    }
                </style>
                <div>
                    <div class="df-progress df-label first">
                        <span class="df-progress-bar"></span>
                    </div>
                    <div class="df-progress">
                        <span class="df-progress-bar"></span>
                    </div>
                </div>
                <div>
                    <div class="df-progress df-label">
                        <span class="df-progress-bar"></span>
                    </div>
                    <div class="df-progress">
                        <span class="df-progress-bar"></span>
                    </div>
                </div>
                <div>
                    <div class="df-progress df-label">
                        <span class="df-progress-bar"></span>
                    </div>
                    <div class="df-progress">
                        <span class="df-progress-bar"></span>
                    </div>
                </div>
                <div>
                    <div class="df-progress df-label">
                        <span class="df-progress-bar"></span>
                    </div>
                    <div class="df-progress">
                        <span class="df-progress-bar"></span>
                    </div>
                    <div class="df-progress">
                        <span class="df-progress-bar"></span>
                    </div>
                    <div class="df-progress">
                        <span class="df-progress-bar"></span>
                    </div>
                </div>
                <div class="df-progress df-button">
                    <span class="df-progress-bar"></span>
                </div>`;

        const loaderEl = document.createElement('div');
        loaderEl.id = 'df-initial-loader-wrapper';
        loaderEl.innerHTML = loaderHTML;

        const script = document.getElementById('df-script');
        if (script) {
            script?.parentNode?.insertBefore(loaderEl, script.nextSibling);
        }
    }

    removeFormLoader() {
        const dfFormLoaderWrapper = this.getHtmlElement('#df-initial-loader-wrapper');
        if (dfFormLoaderWrapper) {
            dfFormLoaderWrapper.remove();
        }
    }

}
